import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import "@v0y4ge/jrni-tokens/tokens.css";
import "@v0y4ge/integration-setup-portal/styles.css";
import "@v0y4ge/gsuite-management/styles.css";
import "./styles.css";
import { App } from "./App";
import { applyTenantThemeBridge } from "./lib/tenant-theme-bridge";

// Tenant brand-kit -> jrni-tokens CSS-var bridge (see lib/tenant-theme-bridge.ts
// for the full mechanism + honest coverage notes). Awaited (with an internal
// 1.5s timeout) before the first render so there's no flash of default JRNI
// purple before the brand color lands; a slow/unreachable branding endpoint
// can never block boot beyond that timeout, and any failure leaves JRNI
// defaults untouched.
const themeBridgeReady = applyTenantThemeBridge().catch(() => {});

// Kill any stale Mock Service Worker left registered by a previous (design-time
// "B") build of CD's app. In production the real backend serves the data; a
// lingering MSW service worker would intercept and break live API calls.
if ("serviceWorker" in navigator) {
  navigator.serviceWorker
    .getRegistrations()
    .then((registrations) => {
      for (const registration of registrations) void registration.unregister();
    })
    .catch(() => {});
}

// Studio API base for the hub/landing/publishing clients (studioRequestOptions
// in cd-app/lib/studio-config.ts) -- unlike the apiRaw-based events/locations/
// dashboard clients, which read VITE_EVENTS_API_BASE_URL straight out of
// import.meta.env inside @v0y4ge/api-client, the publishing-family clients take
// an explicit `{ baseUrl }` and look it up via `window.__STUDIO_API_BASE__`.
// Nothing was ever setting that global, so every deployed tenant fetched those
// endpoints same-origin, got the SPA's own index.html back as a 200, and the
// Zod parse in the landing/hub screens threw -- rendering "This page isn't
// available." for every published event. Bake the same tenant API origin the
// rest of the client already resolves from the composer-generated env. Plain
// origin, no `/t/<slug>` segment: the publishing routes (e.g.
// `/v1/landing/public/<slug>`) are mounted directly on the events worker, and
// `publishingFetch` composes `${baseUrl}${path}` with `path` already carrying
// the full route -- adding a tenant segment here would just double up what the
// worker's own routing already handles. Absence/empty env leaves the global
// unset so the design-time MSW same-origin behavior is untouched.
const studioApiBase = import.meta.env.VITE_EVENTS_API_BASE_URL as string | undefined;
if (studioApiBase) {
  (window as { __STUDIO_API_BASE__?: string }).__STUDIO_API_BASE__ = studioApiBase;
}

const root = document.getElementById("root");

if (!root) {
  throw new Error("root element not found");
}

// Doom-loop guard. CD's not-yet-migrated screen stub (cd-app/shell/screens.tsx
// PreviousScreen) embeds `./legacy-prototype-source/index.html` in an iframe to
// show the previous design. That source dir doesn't exist in production, and the
// Cloudflare Pages SPA fallback serves THIS index.html (200) for the missing
// path — so the iframe loads the whole app again, which renders another stub,
// which iframes again → infinite recursion + blank screen, breaking even the
// migrated screens (events). Until CD drops the iframe vestige, short-circuit
// here: when we're running inside an iframe (the only time this app is framed)
// render a plain placeholder instead of booting the app, ending the recursion
// at depth 1 regardless of route depth.
if (window.self !== window.top) {
  root.innerHTML =
    '<div style="display:flex;align-items:center;justify-content:center;height:100vh;font:13px system-ui,sans-serif;color:#6b7280;text-align:center;padding:40px">This screen has not been migrated yet.</div>';
} else {
  void themeBridgeReady.then(() => {
    createRoot(root).render(
      <StrictMode>
        <App />
      </StrictMode>,
    );
  });
}
